if (!customElements.get('finch-progress-hero')) {
  class FinchProgressHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.slides = Array.from(this.querySelectorAll('[data-fph-slide]'));
      this.viewport = this.querySelector('[data-fph-viewport]');
      this.track = this.querySelector('[data-fph-track]');
      this.previousButton = this.querySelector('[data-fph-previous]');
      this.nextButton = this.querySelector('[data-fph-next]');
      this.indicators = Array.from(this.querySelectorAll('[data-fph-indicator]'));
      this.status = this.querySelector('[data-fph-status]');
      this.primaryHeading = this.querySelector('[data-fph-primary-heading]');
      this.headingTemplates = Array.from(this.querySelectorAll('[data-fph-heading-template]'));

      if (!this.viewport || !this.track || !this.primaryHeading || this.slides.length === 0) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.pauseReasons = new Set();
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.duration = Math.max(1000, Number.parseInt(this.dataset.duration || '6000', 10));
      this.autoplayConfigured = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.elapsed = 0;
      this.completionElapsed = 0;
      this.completionHold = 140;
      this.lastFrameTime = null;
      this.animationFrame = null;
      this.pointerState = null;
      this.playbackControlActive = false;
      this.reducedMotionOverride = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.sectionElement = this.closest('.shopify-section');

      if (this.reducedMotion.matches) this.pauseReasons.add('reduced-motion');
      if (document.hidden) this.pauseReasons.add('document-hidden');

      this.bindEvents();
      this.createObserver();
      this.updateSlides({ announce: false });
      this.syncPlayback();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.cancelFrame();
      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      this.reducedMotion?.removeEventListener('change', this.onReducedMotionChange);
      this.initialized = false;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });

      this.indicators.forEach((indicator, index) => {
        indicator.addEventListener(
          'click',
          () => {
            if (index === this.activeIndex && this.autoplayConfigured) {
              this.togglePlayback();
              return;
            }
            this.goTo(index, { announce: true, reset: true });
          },
          { signal }
        );
      });

      this.addEventListener(
        'pointerenter',
        () => {
          if (this.hoverCapable.matches) this.addPauseReason('hover');
        },
        { signal }
      );
      this.addEventListener(
        'pointerleave',
        () => {
          this.playbackControlActive = false;
          if (this.hoverCapable.matches) this.removePauseReason('hover');
        },
        { signal }
      );
      this.addEventListener(
        'pointerover',
        (event) => {
          if (!this.playbackControlActive) return;
          if (event.target.closest?.('[data-fph-indicator].is-active')) return;
          this.playbackControlActive = false;
          if (this.hoverCapable.matches) this.addPauseReason('hover');
        },
        { signal }
      );
      this.addEventListener(
        'focusin',
        (event) => {
          if (this.playbackControlActive && event.target.closest?.('[data-fph-indicator].is-active')) return;
          this.playbackControlActive = false;
          this.addPauseReason('focus');
        },
        { signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.removePauseReason('focus');
        },
        { signal }
      );

      this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
      this.viewport.addEventListener('pointermove', (event) => this.handlePointerMove(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
      this.viewport.addEventListener('pointercancel', () => this.cancelPointer(), { signal });

      document.addEventListener('visibilitychange', () => this.handleVisibility(), { signal });
      window.addEventListener('pagehide', () => this.addPauseReason('document-hidden'), { signal });
      window.addEventListener('pageshow', () => this.handleVisibility(), { signal });
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });
      document.addEventListener('shopify:block:deselect', (event) => this.handleBlockDeselect(event), { signal });
      document.addEventListener('shopify:section:select', (event) => this.handleSectionSelect(event), { signal });
      document.addEventListener('shopify:section:deselect', (event) => this.handleSectionDeselect(event), { signal });

      this.onReducedMotionChange = (event) => {
        this.reducedMotionOverride = false;
        if (event.matches) this.addPauseReason('reduced-motion');
        else this.removePauseReason('reduced-motion');
        this.updateSlides({ announce: false });
      };
      this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
    }

    createObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.15) this.removePauseReason('offscreen');
          else this.addPauseReason('offscreen');
        },
        { threshold: [0, 0.15] }
      );
      this.intersectionObserver.observe(this);
    }

    handleKeydown(event) {
      const withinControls = event.target.closest?.('[data-fph-controls]');
      if (!withinControls && event.target !== this.viewport) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const pointsForward = event.key === 'ArrowRight' ? !isRtl : isRtl;
        this.move(pointsForward ? 1 : -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, { announce: true, reset: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, { announce: true, reset: true });
      }
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' || event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, [role="button"]')) return;

      this.pointerState = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        deltaY: 0,
        horizontal: null,
      };
      this.viewport.setPointerCapture?.(event.pointerId);
      this.addPauseReason('pointer');
    }

    handlePointerMove(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.id) return;

      this.pointerState.deltaX = event.clientX - this.pointerState.startX;
      this.pointerState.deltaY = event.clientY - this.pointerState.startY;

      if (this.pointerState.horizontal === null) {
        const horizontalDistance = Math.abs(this.pointerState.deltaX);
        const verticalDistance = Math.abs(this.pointerState.deltaY);
        if (Math.max(horizontalDistance, verticalDistance) < 8) return;
        this.pointerState.horizontal = horizontalDistance > verticalDistance;
      }

      if (!this.pointerState.horizontal) {
        this.cancelPointer();
        return;
      }

      if (event.cancelable) event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.id) return;

      const state = this.pointerState;
      const threshold = Math.max(40, this.viewport.clientWidth * 0.1);
      const isRtl = getComputedStyle(this).direction === 'rtl';
      this.pointerState = null;
      this.removePauseReason('pointer');

      if (!state.horizontal || Math.abs(state.deltaX) < threshold) return;
      const swipedLeft = state.deltaX < 0;
      const pointsForward = isRtl ? !swipedLeft : swipedLeft;
      this.move(pointsForward ? 1 : -1);
    }

    cancelPointer() {
      this.pointerState = null;
      this.removePauseReason('pointer');
    }

    handleVisibility() {
      if (document.hidden) this.addPauseReason('document-hidden');
      else this.removePauseReason('document-hidden');
    }

    handleBlockSelect(event) {
      const slide = event.target.closest?.('[data-fph-slide]');
      if (!slide || !this.contains(slide)) return;
      this.addPauseReason('editor-block');
      this.goTo(Number.parseInt(slide.dataset.index, 10), { announce: false, reset: true });
    }

    handleBlockDeselect(event) {
      if (!event.target.closest?.('[data-fph-slide]') || !this.contains(event.target)) return;
      this.removePauseReason('editor-block');
    }

    handleSectionSelect(event) {
      if (event.target !== this.sectionElement) return;
      this.addPauseReason('editor-section');
    }

    handleSectionDeselect(event) {
      if (event.target !== this.sectionElement) return;
      this.removePauseReason('editor-section');
    }

    move(direction) {
      const nextIndex = (this.activeIndex + direction + this.slides.length) % this.slides.length;
      this.goTo(nextIndex, { announce: true, reset: true });
    }

    goTo(index, { announce = false, reset = true } = {}) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;
      this.activeIndex = index;
      if (reset) this.resetProgress();
      this.updateSlides({ announce });
      this.syncPlayback();
    }

    updateSlides({ announce }) {
      const explicitlyPaused = this.pauseReasons.has('user') || this.pauseReasons.has('reduced-motion');
      const activeSlide = this.slides[this.activeIndex];

      activeSlide.classList.add('is-active');
      activeSlide.setAttribute('aria-hidden', 'false');
      activeSlide.inert = false;
      activeSlide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.removeAttribute('tabindex');
      });
      this.updatePrimaryHeading(activeSlide);

      this.slides.forEach((slide, index) => {
        if (index === this.activeIndex) return;

        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
      });

      this.indicators.forEach((indicator, index) => {
        const active = index === this.activeIndex;
        indicator.classList.toggle('is-active', active);
        indicator.toggleAttribute('aria-current', active);
        indicator.querySelector('[data-fph-progress]')?.style.setProperty('transform', `scaleX(${active ? this.progress : 0})`);

        if (active && this.autoplayConfigured) {
          indicator.setAttribute('aria-label', explicitlyPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
          indicator.dataset.playbackState = explicitlyPaused ? 'paused' : 'playing';
        } else {
          indicator.setAttribute(
            'aria-label',
            `${this.dataset.slideLabel} ${index + 1} ${this.dataset.ofLabel} ${this.slides.length}`
          );
          delete indicator.dataset.playbackState;
        }
      });

      if (this.track) this.track.setAttribute('aria-live', this.canRun ? 'off' : 'polite');
      if (announce && this.status) {
        this.status.textContent = `${this.dataset.slideLabel} ${this.activeIndex + 1} ${this.dataset.ofLabel} ${this.slides.length}`;
      }
    }

    updatePrimaryHeading(activeSlide) {
      const targetSlot = activeSlide.querySelector('[data-fph-heading-slot]');
      const template = this.headingTemplates.find(
        (candidate) => Number.parseInt(candidate.dataset.index, 10) === this.activeIndex
      );
      if (!targetSlot || !template) return;

      const fragment = template.content.cloneNode(true);
      this.primaryHeading.replaceChildren(fragment);
      this.primaryHeading.className = `fph__heading inline-richtext ${template.dataset.headingSize || 'h1'}`;
      this.primaryHeading.classList.toggle('visually-hidden', template.dataset.headingVisible !== 'true');
      targetSlot.append(this.primaryHeading);
    }

    togglePlayback() {
      if (!this.autoplayConfigured) return;

      if (this.pauseReasons.has('user')) {
        this.playbackControlActive = true;
        this.pauseReasons.delete('focus');
        this.pauseReasons.delete('hover');
        this.removePauseReason('user');
      } else if (this.pauseReasons.has('reduced-motion')) {
        this.reducedMotionOverride = true;
        this.playbackControlActive = true;
        this.pauseReasons.delete('focus');
        this.pauseReasons.delete('hover');
        this.removePauseReason('reduced-motion');
      } else {
        this.playbackControlActive = false;
        this.addPauseReason('user');
      }
      this.updateSlides({ announce: false });
    }

    addPauseReason(reason) {
      if (this.pauseReasons.has(reason)) return;
      this.pauseReasons.add(reason);
      this.syncPlayback();
    }

    removePauseReason(reason) {
      if (!this.pauseReasons.delete(reason)) return;
      this.syncPlayback();
    }

    get canRun() {
      return this.autoplayConfigured && this.pauseReasons.size === 0;
    }

    get progress() {
      return Math.min(1, this.elapsed / this.duration);
    }

    syncPlayback() {
      if (!this.canRun) {
        this.cancelFrame();
        this.lastFrameTime = null;
      } else if (this.animationFrame === null) {
        this.lastFrameTime = performance.now();
        this.animationFrame = requestAnimationFrame((time) => this.tick(time));
      }
      this.updateSlides({ announce: false });
    }

    tick(time) {
      this.animationFrame = null;
      if (!this.canRun) return;

      const delta = Math.min(100, Math.max(0, time - (this.lastFrameTime || time)));
      this.lastFrameTime = time;

      if (this.elapsed < this.duration) {
        const remaining = this.duration - this.elapsed;
        this.elapsed += Math.min(delta, remaining);
        if (delta > remaining) this.completionElapsed += delta - remaining;
      } else {
        this.completionElapsed += delta;
      }

      this.paintProgress();

      if (this.elapsed >= this.duration && this.completionElapsed >= this.completionHold) {
        const nextIndex = (this.activeIndex + 1) % this.slides.length;
        this.goTo(nextIndex, { announce: false, reset: true });
        return;
      }

      this.animationFrame = requestAnimationFrame((nextTime) => this.tick(nextTime));
    }

    paintProgress() {
      const activeIndicator = this.indicators[this.activeIndex];
      activeIndicator?.querySelector('[data-fph-progress]')?.style.setProperty('transform', `scaleX(${this.progress})`);
    }

    resetProgress() {
      this.elapsed = 0;
      this.completionElapsed = 0;
      this.lastFrameTime = null;
      this.paintProgress();
    }

    cancelFrame() {
      if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  customElements.define('finch-progress-hero', FinchProgressHero);
}
